import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import { Box, CircularProgress } from "@mui/material";

// Workbooks Routes
const WorkbooksScene = lazy(() => import("../scenes/WorkbooksScene/WorkbooksScene"));
const WorkbooksMain = lazy(() => import("../scenes/WorkbooksScene/WorkbooksMain"));
const TemplateGallery = lazy(() => import("../scenes/WorkbooksScene/templates/TemplateGallery"));
const WorkbookSettings = lazy(() => import("../scenes/WorkbooksScene/settings/WorkbookSettings"));
const ExecutionReportPage = lazy(() => import("../scenes/WorkbooksScene/execution/ExecutionReportPage"));
const ExecutionHistory = lazy(() => import("../scenes/WorkbooksScene/execution/ExecutionHistory"));
const CompareExecutions = lazy(() => import("../scenes/WorkbooksScene/execution/CompareExecutions"));
const PipelineIntegration = lazy(() => import("../scenes/WorkbooksScene/settings/PipelineIntegration"));

// Dashboard Routes
const DashboardsMain = lazy(() => import("../scenes/dashboard/DashboardsMain"));
const DashboardDesigner = lazy(() => import("../scenes/dashboard/DashboardDesigner"));
const DashboardViewer = lazy(() => import("../scenes/dashboard/DashboardViewer"));
// §16 interaction prototype. Runs entirely against synthetic fixtures and calls no
// API, so it can be reviewed without a cluster or credentials. Reachable by URL
// only and deliberately absent from the sidebar: it is a review surface, not a
// feature, and §16 implies no production deployment from prototype work.
const QueryStoryGallery = lazy(() => import("../scenes/query-workbench/stories/QueryStoryGallery"));

// Resources (Plan usage + Downloads)
const ResourcesMain = lazy(() => import("../scenes/resources/ResourcesMain"));

// Blend — entity-backed software/host inventory (the semantic estate view)
const BlendInventory = lazy(() => import("../scenes/blend/BlendInventory"));
const ProductDetailPage = lazy(() => import("../scenes/blend/ProductDetailPage"));
const HostDetailPage = lazy(() => import("../scenes/blend/HostDetailPage"));


// Network Observability Routes
const NetworkObservabilityMain = lazy(() => import("../scenes/network-observability/NetworkObservabilityMain"));
const CreateNetworkTestPage = lazy(() => import("../scenes/network-observability/components/CreateNetworkTestPage"));

// Synthetic Monitoring Routes
const SyntheticMain = lazy(() => import("../scenes/synthetic/SyntheticMain"));
const CreateTestPage = lazy(() => import("../scenes/synthetic/components/CreateTestPage"));
const SyntheticTestDetail = lazy(() => import("../scenes/synthetic/insights/SyntheticTestDetail"));
const SyntheticRunReport = lazy(() => import("../scenes/synthetic/insights/SyntheticRunReport"));

// Web Experience (RUM) Routes
const WebExperienceMain = lazy(() => import("../scenes/web-experience/WebExperienceMain"));

// Infrastructure Routes
const InfrastructureMain = lazy(() => import("../scenes/infrastructure/InfrastructureMain"));
const MachineAgentDetail = lazy(() => import("../scenes/infrastructure/MachineAgentDetail"));
const KubernetesMain = lazy(() => import("../scenes/kubernetes/KubernetesMain"));
// Logs page retired — Data Explorer is the unified entry point.
const DataExplorer = lazy(() => import("../scenes/data-explorer/DataExplorerUnified"));

// Auth & Admin
const ProtectedRoute = lazy(() => import("./ProtectedRoute"));
const FeatureGuard = lazy(() => import("./FeatureGuard"));

// Alerts Routes
const AlertsMain = lazy(() => import("../scenes/alerts/AlertsMain"));
const SentinelMain = lazy(() => import("../scenes/sentinel/SentinelMain"));
const VeraScene = lazy(() => import("../scenes/vera/VeraScene"));

// On-Call Routes — promoted out of the /alerts ONCALL tab into its
// own top-level surface. Lives under "Manage" in the sidebar.
const OnCallScene = lazy(() => import("../scenes/oncall/OnCallScene"));

// Settings Routes
const SettingsMain = lazy(() => import("../scenes/settings/SettingsMain"));

// Cloud Integrations (AWS/Azure metric ingestion) — top-level "Manage" surface
// Cloud accounts: the server's capability map picks the existing page (collector v2 off, the
// default) or the new workspace; see CloudIntegrationsPage.
const CloudIntegrationsPage = lazy(() => import("../scenes/integrations/cloud/CloudIntegrationsPage"));

// Profile Route
const ProfilePage = lazy(() => import("../scenes/profile/ProfilePage"));


// Service Browser Route
const ServiceBrowserWrapper = lazy(() => import("../scenes/service-browser/ServiceBrowserWrapper"));
const TraceDetailsPage = lazy(() => import("../scenes/service-browser/TraceDetailsPage"));

// Home Page
const HomePage = lazy(() => import("../scenes/home/HomePage"));

// Contact Channel
const MyTicketsPage = lazy(() => import("../scenes/contact/MyTicketsPage"));
const NewTicketPage = lazy(() => import("../scenes/contact/NewTicketPage"));
const TicketThreadPage = lazy(() => import("../scenes/contact/TicketThreadPage"));

// Onboarding
const OnboardingWizard = lazy(() => import("../scenes/onboarding/OnboardingWizard"));
// Guided setup. The page redirects home unless features.integrationSetup === true, so with the
// flag off (the default) the route is inert: nothing links to it, and a typed-in /setup loads
// the small page chunk, makes no API request and redirects home.
const IntegrationSetupPage = lazy(() => import("../scenes/integrationSetup/IntegrationSetupPage"));

// SaaS Settings
const BillingPage = lazy(() => import("../scenes/settings/components/BillingPage"));
const OrganizationSettings = lazy(() => import("../scenes/settings/components/OrganizationSettings"));
const SettingsLayout = lazy(() => import("../scenes/settings/components/SettingsLayout"));


/**
 * Route-transition fallback. Every routed page is a lazy chunk now, so this
 * shows for the few hundred ms a chunk takes to arrive on first visit to a
 * section (cached thereafter).
 *
 * Deliberately minimal: a spinner centred in the content area, not a full-page
 * skeleton. The sidebar and topbar live outside this boundary and stay put, so
 * a heavier placeholder would read as the whole app reloading.
 */
const RouteFallback = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', width: '100%' }}>
    <CircularProgress size={28} />
  </Box>
);

const AppRoutes = () => {
  return (
    // App-wide safety net: a render crash in any routed page shows a friendly
    // retry fallback (keeps the sidebar/topbar) instead of white-screening.
    <ErrorBoundary label="page">
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Dashboard Routes */}
      <Route path="/dashboards" element={<DashboardsMain />} />
      <Route path="/dashboards/:id" element={<DashboardsMain />} />
      <Route path="/dashboards/:id/edit" element={<DashboardsMain />} />
      <Route path="/designer" element={<DashboardDesigner />} />
      <Route path="/viewer" element={<DashboardViewer />} />
      <Route path="/query-workbench/stories" element={<QueryStoryGallery />} />

      {/* Blend — software/host inventory read from the synthesized entity model */}
      <Route path="/inventory" element={<FeatureGuard feature="inventory"><BlendInventory /></FeatureGuard>} />
      <Route path="/inventory/products/:id" element={<FeatureGuard feature="inventory"><ProductDetailPage /></FeatureGuard>} />
      <Route path="/inventory/hosts/:id" element={<FeatureGuard feature="inventory"><HostDetailPage /></FeatureGuard>} />

      {/* Metrics Explorer lives inside Data Explorer now (its "Builder");
          keep the old deep link working. */}
      <Route path="/explorer" element={<Navigate to="/data-explorer" replace />} />

      {/* Workbooks Routes */}
      <Route path="/workbooks" element={<FeatureGuard feature="workbooks"><WorkbooksMain /></FeatureGuard>} />
      <Route path="/workbooks/templates" element={<TemplateGallery />} />
      <Route path="/workbooks/designer/:id" element={<WorkbooksScene />} />
      <Route path="/workbooks/settings" element={<WorkbookSettings />} />
      <Route path="/workbooks/pipelines" element={<PipelineIntegration />} />
      <Route path="/workbooks/:id/executions" element={<ExecutionHistory />} />
      <Route path="/workbooks/:id/executions/compare" element={<CompareExecutions />} />
      <Route path="/workbooks/executions/:executionId" element={<ExecutionReportPage />} />


      {/* Applications Route (Service Browser) */}
      <Route path="/applications/*" element={<ServiceBrowserWrapper />} />
      <Route path="/service-browser/*" element={<ServiceBrowserWrapper />} />

      {/* Trace details — single trace as a real page (replaces the
          modal drawer that used to slide over the operations table /
          logs / workbook execution panel). Accepts an optional
          {initialTrace} via location.state so the caller can skip the
          first fetch when they already have the row. */}
      <Route path="/traces/:traceId" element={<TraceDetailsPage />} />

      {/* Resources — plan consumption + downloads catalog.
          Replaces the old /plugins page; the agent-management bits moved
          into each Application's sub-tabs (Agents / Discover / Collectors). */}
      <Route path="/resources" element={<ResourcesMain />} />

      {/* Infrastructure Routes */}
      <Route path="/infrastructure" element={<InfrastructureMain />} />
      <Route path="/infrastructure/servers/:id" element={<InfrastructureMain />} />
      <Route path="/infrastructure/containers/:id" element={<InfrastructureMain />} />
      <Route path="/infrastructure/machine-agents/:agentId" element={<MachineAgentDetail />} />

      {/* Kubernetes Routes */}
      <Route path="/kubernetes" element={<KubernetesMain />} />
      <Route path="/kubernetes/nodes" element={<KubernetesMain />} />
      <Route path="/kubernetes/workloads" element={<KubernetesMain />} />
      <Route path="/kubernetes/services" element={<KubernetesMain />} />
      <Route path="/kubernetes/storage" element={<KubernetesMain />} />
      <Route path="/kubernetes/events" element={<KubernetesMain />} />

      {/* Logs removed — use Data Explorer for log queries. */}

      {/* Data Explorer Routes */}
      <Route path="/data-explorer" element={<DataExplorer />} />

      {/* Network Observability Routes */}
      <Route path="/network" element={<NetworkObservabilityMain />} />
      <Route path="/network/dashboard" element={<NetworkObservabilityMain />} />
      <Route path="/network/topology" element={<NetworkObservabilityMain />} />
      <Route path="/network/agents" element={<NetworkObservabilityMain />} />
      {/* ENTITY ROUTES — deep-linkable, shareable, and they carry the
          investigation context in the query string. Before this, nothing in the
          feature except a test edit form had a URL, so an operator could not
          send a colleague "look at this agent in this window". */}
      <Route path="/network/agents/:agentId" element={<NetworkObservabilityMain />} />
      <Route path="/network/tests/:testId/runs" element={<NetworkObservabilityMain />} />
      <Route path="/network/paths/:sourceAgentId/:targetHost" element={<NetworkObservabilityMain />} />
      <Route path="/network/devices/:ip" element={<NetworkObservabilityMain />} />
      <Route path="/network/path-analysis" element={<NetworkObservabilityMain />} />
      {/* §13's sixth top-level area: chronological changes + alert transitions. */}
      <Route path="/network/changes" element={<NetworkObservabilityMain />} />
      {/* P4-7 — passive dependency, traffic-matrix and top-talker views. */}
      <Route path="/network/dependencies" element={<NetworkObservabilityMain />} />
      <Route path="/network/events" element={<NetworkObservabilityMain />} />
      <Route path="/network/tests" element={<NetworkObservabilityMain />} />
      <Route path="/network/tests/create" element={<CreateNetworkTestPage />} />
      <Route path="/network/tests/edit/:testId" element={<CreateNetworkTestPage />} />
      <Route path="/network/metrics" element={<NetworkObservabilityMain />} />
      <Route path="/network/snmp" element={<NetworkObservabilityMain />} />
      <Route path="/network/discovery" element={<NetworkObservabilityMain />} />
      <Route path="/network/mesh" element={<NetworkObservabilityMain />} />

      {/* Synthetic Monitoring Routes */}
      <Route path="/synthetic" element={<SyntheticMain />} />
      <Route path="/synthetic/dashboard" element={<SyntheticMain />} />
      <Route path="/synthetic/analytics" element={<SyntheticMain />} />
      <Route path="/synthetic/tests" element={<SyntheticMain />} />
      <Route path="/synthetic/agents" element={<SyntheticMain />} />
      <Route path="/synthetic/credentials" element={<SyntheticMain />} />
      <Route path="/synthetic/history" element={<SyntheticMain />} />
      <Route path="/synthetic/executions/:executionId" element={<SyntheticRunReport />} />
      <Route path="/synthetic/tests/create" element={<CreateTestPage />} />
      <Route path="/synthetic/tests/edit/:testId" element={<CreateTestPage />} />
      <Route path="/synthetic/tests/:testId" element={<SyntheticTestDetail />} />

      {/* Web Experience (RUM) Routes */}
      <Route path="/web-experience" element={<WebExperienceMain />} />
      <Route path="/web-experience/overview" element={<WebExperienceMain />} />
      <Route path="/web-experience/dashboard" element={<WebExperienceMain />} />
      <Route path="/web-experience/sessions" element={<WebExperienceMain />} />
      <Route path="/web-experience/journeys" element={<WebExperienceMain />} />
      <Route path="/web-experience/performance" element={<WebExperienceMain />} />
      <Route path="/web-experience/performance/vitals" element={<WebExperienceMain />} />
      <Route path="/web-experience/performance/pages" element={<WebExperienceMain />} />
      <Route path="/web-experience/funnels" element={<WebExperienceMain />} />
      <Route path="/web-experience/errors" element={<WebExperienceMain />} />
      <Route path="/web-experience/geography" element={<WebExperienceMain />} />
      <Route path="/web-experience/user-experience" element={<WebExperienceMain />} />
      <Route path="/web-experience/apps" element={<WebExperienceMain />} />

      {/* Alerts Routes */}
      <Route path="/alerts" element={<AlertsMain />} />

      {/* VerOps Sentinel — AI Control Plane / AI-SRE */}
      {/* Sentinel owns its own sub-tree so each surface is a real, linkable path
          (/sentinel/runs/:executionId, /sentinel/explorer, …) rather than a ?tab= value
          on one page. The trailing splat is required for the nested Routes to match. */}
      <Route path="/sentinel/*" element={<FeatureGuard feature="sentinel"><SentinelMain /></FeatureGuard>} />

      {/* Vera — conversational AI for the platform */}
      <Route path="/vera" element={<FeatureGuard feature="vera"><VeraScene /></FeatureGuard>} />

      {/* Response (formerly "On-Call") — was a tab inside /alerts,
          then a top-level /oncall scene. War Rooms 2.0 promoted the
          surface to a full incident-response umbrella: schedules,
          escalation, and now war rooms all live under /response.
          /oncall stays as a legacy alias so deep-links don't 404. */}
      <Route path="/response" element={<OnCallScene />} />
      <Route path="/response/rooms/:guid" element={<OnCallScene />} />
      <Route path="/oncall" element={<OnCallScene />} />

      {/* Profile Route */}
      <Route path="/profile" element={<ProfilePage />} />

      {/* Onboarding Route */}
      <Route path="/onboarding" element={<OnboardingWizard />} />
      <Route path="/setup" element={<IntegrationSetupPage />} />
      <Route path="/setup/:sessionId" element={<IntegrationSetupPage />} />

      {/* Cloud Integrations (admin-gated) */}
      <Route path="/integrations" element={
        <ProtectedRoute adminRequired={true}>
          <CloudIntegrationsPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations/cloud" element={
        <ProtectedRoute adminRequired={true}>
          <CloudIntegrationsPage />
        </ProtectedRoute>
      } />

      {/* Settings Routes */}
      <Route path="/settings" element={
        <ProtectedRoute adminRequired={true}>
          <SettingsMain />
        </ProtectedRoute>
      } />
      <Route path="/settings/organization" element={
        <ProtectedRoute adminRequired={true}>
          <SettingsLayout><OrganizationSettings /></SettingsLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/billing" element={
        <ProtectedRoute adminRequired={true}>
          <SettingsLayout><BillingPage /></SettingsLayout>
        </ProtectedRoute>
      } />

      {/* Contact channel — user-facing communication with operator team. */}
      <Route path="/contact" element={<MyTicketsPage />} />
      <Route path="/contact/new" element={<NewTicketPage />} />
      <Route path="/contact/tickets/:id" element={<TicketThreadPage />} />

      {/* Root route - Home Page */}
      <Route path="/" element={<HomePage />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
  );
};

export default AppRoutes;
